import type { VectorValue as VectorValueType } from "@/lib/node-description/value-types"
import { isScalarType, isVectorType } from "@/lib/node-description/value-types"
import type { NodeHandleTone } from "./nodes/node-appearance"
import { NodePill } from "./nodes/primitives/node-pill"
import { ScalarValuePill } from "./pills"
import { VectorValue } from "./vector-value"

interface InlineValueProps {
	paramName: string
	value: unknown
	type: string
	onChange: (value: unknown) => void
	isConnected: boolean
	hasResolvedValue: boolean
	resolvedValue: unknown
	sourceLabel: string
	showLabel?: boolean
	variant?: "canvas" | "sidebar"
	handleTone?: NodeHandleTone
}

export const humanizeParameterName = (name: string) => {
	const label = name.replaceAll(/_+/g, " ").trim()
	return label ? label[0]!.toUpperCase() + label.slice(1) : label
}

/**
 * Inline parameter renderer.
 * Scalar -> editable scalar pill
 * Vector -> three scalar pills
 * Geometry -> empty socket pill or connected pill
 */
export const InlineValue = ({
	paramName,
	value,
	type,
	onChange,
	isConnected,
	hasResolvedValue,
	resolvedValue,
	sourceLabel,
	showLabel: _showLabel = true,
	variant = "canvas",
	handleTone: _handleTone = "neutral",
}: InlineValueProps) => {
	const displayValue = isConnected ? resolvedValue : value
	const isWaiting = isConnected && !hasResolvedValue
	const isVector = isVectorType(type)
	const isScalar = isScalarType(type)
	const valueContent = isWaiting ? (
		<span data-node-input-waiting className="min-w-[1ch] text-center">
			…
		</span>
	) : isVector ? (
		<VectorValue
			paramName={paramName}
			value={displayValue}
			isConnected={false}
			sourceLabel={sourceLabel}
			onChange={(newValue: VectorValueType) => onChange(newValue)}
			variant="named"
			disabled={isConnected}
		/>
	) : isScalar ? (
		<ScalarValuePill
			value={displayValue}
			type={type}
			variant="named"
			disabled={isConnected}
			onChange={onChange}
		/>
	) : (
		<span className="max-w-32 truncate text-[12px] leading-[normal] font-medium">
			{isConnected ? sourceLabel || "Connected" : "—"}
		</span>
	)
	const content = (
		<>
			<span className="text-[12px] leading-[normal] font-semibold whitespace-nowrap">
				{humanizeParameterName(paramName)}
			</span>
			<span aria-hidden className="text-[var(--node-text)]/45">
				|
			</span>
			{valueContent}
		</>
	)

	if (variant === "sidebar") {
		return (
			<span
				aria-disabled={isConnected}
				className="inline-flex min-h-7 items-center gap-1.5 rounded-lg bg-muted/60 px-2 text-foreground"
			>
				{content}
			</span>
		)
	}

	return (
		<NodePill
			tone={
				isConnected ? "connected" : isScalar || isVector ? "scalar" : "socket"
			}
			interactive={!isConnected && (isScalar || isVector)}
			className={isConnected ? "opacity-[0.65]" : undefined}
		>
			<span
				data-node-parameter-pill={paramName}
				aria-disabled={isConnected}
				className="inline-flex items-center gap-1.5"
			>
				{content}
			</span>
		</NodePill>
	)
}
